import { useState, useEffect } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { MdCheckBoxOutlineBlank, MdCheckBox } from "react-icons/md";
import { toast } from "react-toastify";
import { BsQrCode } from "react-icons/bs";
import { LuClipboardCopy } from "react-icons/lu";
import { IoTrashOutline } from "react-icons/io5";
import { FaPlusSquare } from "react-icons/fa";
import { RxDragHandleDots1 } from "react-icons/rx";
import { TbTableImport } from "react-icons/tb";
import QrModal from "./QrModal";
import ConfirmModal from "./ConfirmModal";
import ImportTestUsersModal from "./ImportTestUsersModal";
import CttCodeEditModal from "./CttCodeEditModal";
import RequestModal from "./RequestModal";
import SectionEditModal from "./SectionEditModal";
import SectionPickerModal from "./SectionPickerModal";
import SectionSelectModal from "./SectionSelectModal";
import RequestFormFieldsTable from "../tables/RequestFormFieldsTable";
import Enums from "../../enums";
import {
  formatDdHhMmFromMinutes,
  parseDdHhMmToMinutes,
} from "@/utils/testTimeoutDisplayHelpers";
import { getLanguageAdminLabel } from "@/utils/languageUtils";
import {
  applyCttCodeActiveToggle,
  cttCodeDatesForApi,
} from "@/utils/cttCodeWindow";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";
import {
  mergeEmailTriggers,
  RECIPIENT_TYPE_OPTIONS,
  type CttEmailTrigger,
  type EmailTemplate,
} from "@/utils/emailTemplate";

const CTT_CONTENT_TAB_ROWS = [
  {
    field: "startTestButtonContentId",
    label: "Request Test Button Text",
    contentTypeId: Enums.CONTENT_TYPE.StartTestButton,
    cellClassName: "contentSelectionCell",
  },
  {
    field: "requestFormInstructionContentId",
    label: "Request Form Instructions",
    contentTypeId: Enums.CONTENT_TYPE.RequestFormInstructions,
    cellClassName: "noPad contentSelectionCell",
  },
  {
    field: "languagePromptContentId",
    label: "Language Prompt",
    contentTypeId: Enums.CONTENT_TYPE.LanguagePrompt,
    cellClassName: "contentSelectionCell",
  },
  {
    field: "restartPageContentId",
    label: "Restart Page",
    contentTypeId: Enums.CONTENT_TYPE.RestartPage,
    cellClassName: "contentSelectionCell",
  },
  {
    field: "codeTimedOutContentId",
    label: "Code Timed Out",
    contentTypeId: Enums.CONTENT_TYPE.CttCodeTimedOut,
    cellClassName: "contentSelectionCell",
  },
  {
    field: "codeEmbargoContentId",
    label: "Code Embargo",
    contentTypeId: Enums.CONTENT_TYPE.CttCodeEmbargoed,
    cellClassName: "contentSelectionCell",
  },
];

function formatCttCodeDateTime(utcIso) {
  if (!utcIso) return "—";
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function TestTypeInfoModal(props) {
  const [formData, setFormData] = useState(null);
  const [activeTestType, setActiveTestType] = useState(null);
  const [codeTimeoutString, updateCodeTimeoutString] = useState("");
  const [testTimeoutString, updateTestTimeoutString] = useState("");
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrContent, setQrContent] = useState("");
  const [cttCodes, setCttCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [cttCodeCounts, setCttCodeCounts] = useState(null);
  const [isCttCodeEditOpen, setIsCttCodeEditOpen] = useState(false);
  const [isReusableCodeRequestOpen, setIsReusableCodeRequestOpen] = useState(false);
  const [editingCttCode, setEditingCttCode] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showImportTestUsersModal, setShowImportTestUsersModal] = useState(false);
  const [showSectionEditModal, setShowSectionEditModal] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(null);
  const [showSectionPickerModal, setShowSectionPickerModal] = useState(false);
  const [contentSectionId, setContentSectionId] = useState(null);
  const [contentSectionName, setContentSectionName] = useState("");
  const [sectionContentId, setSectionContentId] = useState(null);
  const [contentSelectTargetField, setContentSelectTargetField] = useState(null);
  const [contentSelectTypeId, setContentSelectTypeId] = useState(null);
  const [contentSelectCurrentId, setContentSelectCurrentId] = useState(null);
  const [contentSelectScope, setContentSelectScope] = useState(null);
  const [sectionRestartContentSectionId, setSectionRestartContentSectionId] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingDefaultSectionContentId, setEditingDefaultSectionContentId] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [showSectionActionConfirm, setShowSectionActionConfirm] = useState(false);
  const [sectionActionTarget, setSectionActionTarget] = useState(null);
  const [sectionActionUsesCustomContent, setSectionActionUsesCustomContent] = useState(false);
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [companyEmailTemplates, setCompanyEmailTemplates] = useState<EmailTemplate[]>([]);


  // Fetch test type data when modal opens
  useEffect(() => {
    fetchTestTypeData();
  }, [props.cttId]);

  // Load non-archived test counts only when the Codes tab is active
  useEffect(() => {
    if (activeTab === "codes" && props.cttId) {
      fetchCttCodeCounts(props.cttId);
    }
  }, [activeTab, props.cttId]);

  // Handle escape key to close modal
  useEffect(() => {
    function handleEscapeKey(event) {
      if (event.key === 'Escape' && props.cttId && !showDeleteConfirm && !isReusableCodeRequestOpen) {
        handleClose();
      }
    }

    if (props.cttId) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [props.cttId, showDeleteConfirm, isReusableCodeRequestOpen]);

  async function fetchTestTypeData() {
    if (!props.cttId) return;

    if (props.updateSpinner)
      props.updateSpinner(true);
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyTestType/${props.cttId}`);
      if (resp && resp.ok) {
        const testTypeData = resp.jsonData || resp.data;
        const cttId = testTypeData?.companyTestType?.id || props.cttId;
        const normalizedData = {
          ...testTypeData,
          emailTriggers: mergeEmailTriggers(testTypeData?.emailTriggers, cttId),
        };
        setActiveTestType(normalizedData);
        setFormData(normalizedData);
        if (normalizedData?.companyTestType?.companyId) {
          loadCompanyEmailTemplatesAsync(normalizedData.companyTestType.companyId);
        }
        updateCodeTimeoutString(formatDdHhMmFromMinutes(testTypeData.companyTestType.codeTimeoutMinutes));
        setHasChanges(false);
        await fetchCttCodes(cttId);
        return testTypeData;
      } else {
        toast.error("Failed to load test type data");
        props.onClose();
        return null;
      }
    } catch (error) {
      console.error("Error fetching test type data:", error);
      toast.error("Error loading test type data");
      props.onClose();
      return null;
    } finally {
      if (props.updateSpinner)
        props.updateSpinner(false);
    }
  }


  function updateTestAsync(e) {
    let propertyName =
      e.currentTarget.getAttribute("name") ||
      e.currentTarget.getAttribute("data-name");
    if (!propertyName) return;
    const dataType = e.currentTarget.getAttribute("data-type");
    let value = e.currentTarget.value;

    if (dataType === "int") {
      if (!isNaN(value)) value = parseInt(value, 10);
      else value = 0;
    }
    if (dataType === "toggle") {
      const nestedProperty = propertyName
        .split(".")
        .reduce((o, i) => o[i], formData);
      value = !nestedProperty;
    }

    let updatedData = { ...formData };
    if (propertyName.startsWith("companyTestType.")) {
      propertyName = propertyName.replace("companyTestType.", "");
      updatedData.companyTestType = { ...updatedData.companyTestType, [propertyName]: value };
    } else updatedData[propertyName] = value;

    setFormData(updatedData);
    setHasChanges(true);
  }

  // No-op: onBlur removed

  function onChangeCodeTimeout(e, propertyName) {
    const totalMinutes = parseDdHhMmToMinutes(e.currentTarget.value);

    const updatedTestType = {
      ...formData,
      companyTestType: {
        ...formData.companyTestType,
        [propertyName]: totalMinutes
      }
    };
    setFormData(updatedTestType);
    setHasChanges(true);
  }

  async function loadCompanyEmailTemplatesAsync(companyId: number) {
    try {
      const resp = await RightTimeApi.getAsync(`/Company/${companyId}/Email`);
      if (resp?.ok && resp.jsonData?.selectableTemplates) {
        setCompanyEmailTemplates(resp.jsonData.selectableTemplates);
      } else {
        setCompanyEmailTemplates([]);
      }
    } catch (error) {
      console.error("Failed to load company email templates:", error);
      setCompanyEmailTemplates([]);
    }
  }

  function updateEmailTrigger(eventType: string, patch: Partial<CttEmailTrigger>) {
    setFormData((prev) => {
      if (!prev) return prev;
      const emailTriggers = (prev.emailTriggers || []).map((trigger) =>
        trigger.eventType === eventType ? { ...trigger, ...patch } : trigger
      );
      return { ...prev, emailTriggers };
    });
    setHasChanges(true);
  }

  function updateStatusEmailConfig(statusId: number, patch: Record<string, unknown>) {
    setFormData((prev) => {
      if (!prev?.statuses) return prev;
      const statuses = prev.statuses.map((status) =>
        Number(status.id) === Number(statusId) ? { ...status, ...patch } : status
      );
      return { ...prev, statuses };
    });
    setHasChanges(true);
  }

  function toggleEmailTriggerEnabled(eventType: string) {
    const trigger = formData?.emailTriggers?.find((t) => t.eventType === eventType);
    updateEmailTrigger(eventType, { isEnabled: !trigger?.isEnabled });
  }

  function getCompanyEmailTemplateById(templateId?: number | null) {
    if (!templateId) return null;
    return companyEmailTemplates.find((t) => Number(t.id) === Number(templateId)) || null;
  }

  async function saveTestAsync() {
    if (!formData || !formData.companyTestType) return;
    setIsSaving(true);
    try {
      const normalizedEnabledLanguages = [...new Set(
        (formData?.companyTestType?.enabledLanguages || [])
          .map((id) => Number(id))
          .filter((id) => id > 0)
      )];

      const normalizedSections = getOrderedSections(formData?.sections || []).map((section, index) => ({
        ...section,
        sectionId: getSectionId(section),
        id: getSectionId(section),
        order: (index + 1) * 10,
        Order: (index + 1) * 10,
      }));

      const updatedFormData = {
        ...formData,
        id: formData.companyTestType.id,
        sections: normalizedSections,
        companyTestType: {
          ...formData.companyTestType,
          enabledLanguages: normalizedEnabledLanguages,
          codeTimeoutMinutes: parseDdHhMmToMinutes(codeTimeoutString),
        },
      };
      setFormData(updatedFormData);

      const resp = await RightTimeApi.putAsync("/CompanyTestType", updatedFormData);
      if (resp && resp.ok) {
        await fetchTestTypeData();
        toast.success("Test saved successfully");
        setHasChanges(false);
        if (props.updateData)
          props.updateData();
        handleClose();
      } else {
        toast.error("Failed to save test");
      }
    } catch (error) {
      console.error("Error saving test:", error);
      toast.error("An error occurred while saving the test");
    } finally {
      setIsSaving(false);
    }
  }

  function handleLanguageToggle(e) {
    const langId = parseInt(e.currentTarget.getAttribute("data-language"), 10);
    const enabledLanguages = formData.companyTestType.enabledLanguages || [];
    let newEnabledLanguages;
    if (enabledLanguages.includes(langId)) {
      newEnabledLanguages = enabledLanguages.filter(id => id !== langId);
    } else {
      newEnabledLanguages = [...enabledLanguages, langId];
    }
    setFormData({
      ...formData,
      companyTestType: {
        ...formData.companyTestType,
        enabledLanguages: newEnabledLanguages
      }
    });
    setHasChanges(true);
  }

  function onSectionInstructionContentChange(sectionId, selectedContentId) {
    const contentId = Number(selectedContentId) || 0;
    setFormData((prev) => {
      if (!prev?.sections) return prev;
      return {
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === sectionId
            ? { ...section, instructionContentId: contentId }
            : section
        )
      };
    });
    setHasChanges(true);
  }

  function onOpenSectionEditModal(section) {
    const targetSectionId = Number(section?.sectionId || section?.id || 0) || null;
    const defaultContentId = Number(section?.defaultSectionContentId || section?.DefaultSectionContentId || 0) || null;
    const contentId = Number(
      section?.customSectionId ||
      section?.sectionContentId ||
      section?.SectionContentId ||
      section?.CustomSectionId ||
      section?.defaultSectionContentId ||
      section?.DefaultSectionContentId ||
      0
    ) || null;

    if (!contentId) {
      toast.info("No section content is currently selected for this section.");
      return;
    }

    setEditingSectionId(targetSectionId);
    setEditingDefaultSectionContentId(defaultContentId);
    setSectionContentId(contentId);
    setShowSectionEditModal(true);
  }

  function onCloseSectionEditModal() {
    setShowSectionEditModal(false);
    setEditingSectionId(null);
    setEditingDefaultSectionContentId(null);
  }

  function onSectionContentForked(forked) {
    const forkedSectionId = Number(forked?.sectionId || 0) || Number(editingSectionId || 0);
    const forkedContentId = Number(forked?.sectionContentId || 0);
    const forkedName = typeof forked?.sectionContentName === "string" ? forked.sectionContentName : "";

    if (!forkedSectionId || !forkedContentId) return;

    setSectionContentId(forkedContentId);
    setFormData((prev) => {
      if (!prev?.sections) return prev;

      return {
        ...prev,
        sections: prev.sections.map((section) => {
          const sectionId = Number(section?.sectionId || section?.id || 0);
          if (sectionId !== forkedSectionId) return section;

          return {
            ...section,
            customSectionId: forkedContentId,
            sectionContentId: forkedContentId,
            customizedSectionName: forkedName || section.customizedSectionName || "",
            sectionContentName: forkedName || section.sectionContentName || "",
          };
        })
      };
    });

    setHasChanges(true);
  }

  function onOpenSectionSelectModal(e, section) {
    e.stopPropagation();
    const sectionId = Number(section?.sectionId || section?.id || 0) || null;
    const customId = Number(
      section?.sectionContentId ||
      section?.customSectionId ||
      section?.SectionContentId ||
      section?.CustomSectionId ||
      0
    ) || null;

    setContentSectionId(sectionId);
    setContentSectionName(section?.name || section?.sectionName || section?.SectionName || "");
    setSectionContentId(customId);
    setSelectionMode("section");
    setShowSelectionModal(true);
  }

  function onOpenNextSectionSelectModal(e) {
    e.stopPropagation();
    if (!formData?.sections?.length) return;

    const nextSection = formData.sections
      .slice()
      .sort((a, b) => a.order - b.order)
      .find((section) => {
        const assignedContentId = Number(
          section?.customSectionId ||
          section?.sectionContentId ||
          section?.SectionContentId ||
          section?.CustomSectionId ||
          0
        );
        return assignedContentId <= 0;
      }) || formData.sections.slice().sort((a, b) => a.order - b.order)[0];

    onOpenSectionSelectModal(e, nextSection);
  }

  function onOpenSectionPickerModal(e) {
    e.stopPropagation();
    setShowSectionPickerModal(true);
  }

  async function onSelectSectionToAdd(section) {
    const sectionId = Number(section?.sectionId || section?.id || 0);
    const companyTestTypeId = Number(formData?.companyTestType?.id || props.cttId || 0);
    if (!sectionId || !companyTestTypeId) return false;

    if (typeof props.updateSpinner === "function") props.updateSpinner(true);
    try {
      const resp = await RightTimeApi.postAsync(`/CompanyTestType/${companyTestTypeId}/Section`, { sectionId });
      if (!resp?.ok) {
        toast.error("Failed to add section.");
        return false;
      }

      await fetchTestTypeData();
      if (props.updateData) props.updateData();
      toast.success("Section added.");
      return true;
    } catch (error) {
      console.error("Error adding section:", error);
      toast.error("Error adding section.");
      return false;
    } finally {
      if (typeof props.updateSpinner === "function") props.updateSpinner(false);
    }
  }

  function onCloseSelectionModal() {
    setShowSelectionModal(false);
    setSelectionMode(null);
    setContentSectionId(null);
    setContentSectionName("");
    setSectionContentId(null);
    setContentSelectScope(null);
    setSectionRestartContentSectionId(null);
  }

  async function onSelectSectionRestartContent(content) {
    const selectedId = getContentId(content);
    const targetSectionId = Number(sectionRestartContentSectionId || 0);
    if (!selectedId || !targetSectionId || !props?.cttId || !Array.isArray(formData?.sections)) {
      toast.error("Unable to select restart page content right now.");
      return false;
    }

    const contentName = getContentName(content);
    const updatedSections = formData.sections.map((section) => {
      const sectionId = getSectionId(section);
      if (sectionId !== targetSectionId) return section;

      return {
        ...section,
        restartPageContentId: selectedId,
        RestartPageContentId: selectedId,
        restartPageContentName: contentName,
        RestartPageContentName: contentName,
      };
    });

    try {
      await persistSectionOrderAsync(getOrderedSections(updatedSections));
      onCloseSelectionModal();
      await fetchTestTypeData();
      if (props.updateData) props.updateData();
      return true;
    } catch (error) {
      console.error("Failed to persist section restart page content", error);
      toast.error("Failed to apply restart page content.");
      return false;
    }
  }

  async function onDeselectSectionRestartContent() {
    const targetSectionId = Number(sectionRestartContentSectionId || 0);
    if (!targetSectionId || !props?.cttId || !Array.isArray(formData?.sections)) {
      toast.error("Unable to clear restart page content right now.");
      return false;
    }

    const updatedSections = formData.sections.map((section) => {
      const sectionId = getSectionId(section);
      if (sectionId !== targetSectionId) return section;

      return {
        ...section,
        restartPageContentId: null,
        RestartPageContentId: null,
        restartPageContentName: "",
        RestartPageContentName: "",
      };
    });

    try {
      await persistSectionOrderAsync(getOrderedSections(updatedSections));
      onCloseSelectionModal();
      await fetchTestTypeData();
      if (props.updateData) props.updateData();
      return true;
    } catch (error) {
      console.error("Failed to clear section restart page content", error);
      toast.error("Failed to clear restart page content.");
      return false;
    }
  }

  function resolveContentSelectHandler() {
    if (selectionMode === "section") return onSelectCustomSection;
    if (contentSelectScope === "sectionRestart") return onSelectSectionRestartContent;
    return onSelectContentRecord;
  }

  function resolveContentDeselectHandler() {
    if (selectionMode === "section") return onDeselectCustomSection;
    if (contentSelectScope === "sectionRestart") return onDeselectSectionRestartContent;
    return onDeselectContentRecord;
  }

  async function onSelectCustomSection(customSection) {
    const customId = Number(
      customSection?.sectionContentId ||
      customSection?.SectionContentId ||
      customSection?.customSectionId ||
      customSection?.CustomSectionId ||
      customSection?.id ||
      customSection?.Id ||
      0
    ) || 0;
    const customName =
      customSection?.sectionContentName ||
      customSection?.SectionContentName ||
      customSection?.customizedSectionName ||
      customSection?.CustomName ||
      customSection?.customName ||
      customSection?.name ||
      "";

    if (!props?.cttId || !contentSectionId || !Array.isArray(formData?.sections)) {
      toast.error("Unable to select section content right now.");
      return false;
    }

    const updatedSections = formData.sections.map((section) => {
      const sectionId = Number(section?.sectionId || section?.id || 0);
      if (sectionId !== Number(contentSectionId)) return section;

      return {
        ...section,
        customSectionId: customId,
        CustomSectionId: customId,
        sectionContentId: customId,
        SectionContentId: customId,
        customizedSectionName: customName,
        sectionContentName: customName,
      };
    });

    try {
      await persistSectionOrderAsync(getOrderedSections(updatedSections));
      onCloseSelectionModal();
      await fetchTestTypeData();
      if (props.updateData) props.updateData();
      return true;
    } catch (error) {
      console.error("Failed to persist selected section content", error);
      toast.error("Failed to apply selected section content.");
      return false;
    }
  }

  async function onDeselectCustomSection() {
    if (!props?.cttId || !contentSectionId || !Array.isArray(formData?.sections)) {
      toast.error("Unable to clear section content right now.");
      return false;
    }

    const updatedSections = formData.sections.map((section) => {
      const sectionId = Number(section?.sectionId || section?.id || 0);
      if (sectionId !== Number(contentSectionId)) return section;

      return {
        ...section,
        customSectionId: null,
        CustomSectionId: null,
        sectionContentId: null,
        SectionContentId: null,
        customizedSectionName: "",
        sectionContentName: "",
      };
    });

    try {
      await persistSectionOrderAsync(getOrderedSections(updatedSections));
      onCloseSelectionModal();
      await fetchTestTypeData();
      if (props.updateData) props.updateData();
      return true;
    } catch (error) {
      console.error("Failed to clear selected section content", error);
      toast.error("Failed to clear selected section content.");
      return false;
    }
  }

  function onClearCustomSection(e, targetSection) {
    e.stopPropagation();

    const targetId = Number(targetSection?.sectionId || targetSection?.id || 0);
    if (!targetId) return;

    setFormData((prev) => {
      if (!prev?.sections) return prev;

      return {
        ...prev,
        sections: prev.sections.map((section) => {
          const sectionId = Number(section?.sectionId || section?.id || 0);
          if (sectionId !== targetId) return section;

          return {
            ...section,
            customSectionId: null,
            sectionContentId: null,
            customizedSectionName: "",
            sectionContentName: "",
          };
        }),
      };
    });

    setHasChanges(true);
  }

  function getSectionActionState(section) {
    const defaultContentId = Number(section?.defaultSectionContentId || section?.DefaultSectionContentId || 0);
    const assignedContentId = Number(
      section?.customSectionId ||
      section?.sectionContentId ||
      section?.SectionContentId ||
      section?.CustomSectionId ||
      0
    );
    const isUsingMasterContent = defaultContentId > 0 && assignedContentId > 0 && assignedContentId === defaultContentId;
    const usesCustomContent = assignedContentId > 0 && (!defaultContentId || assignedContentId !== defaultContentId);
    return {
      defaultContentId,
      assignedContentId,
      isUsingMasterContent,
      usesCustomContent,
    };
  }

  function onOpenSectionActionModal(e, section) {
    e.stopPropagation();
    const targetId = Number(section?.sectionId || section?.id || 0);
    if (!targetId) return;

    const sectionState = getSectionActionState(section);
    setSectionActionTarget(section);
    setSectionActionUsesCustomContent(sectionState.usesCustomContent);
    setShowSectionActionConfirm(true);
  }

  function closeSectionActionModal() {
    setShowSectionActionConfirm(false);
    setSectionActionTarget(null);
    setSectionActionUsesCustomContent(false);
  }

  function applySectionActionById(targetId, nextValues) {
    setFormData((prev) => {
      if (!prev?.sections) return prev;

      return {
        ...prev,
        sections: prev.sections.map((section) => {
          const sectionId = Number(section?.sectionId || section?.id || 0);
          if (sectionId !== targetId) return section;

          return {
            ...section,
            ...nextValues,
          };
        }),
      };
    });
    setHasChanges(true);
  }

  async function persistSectionActionAsync(targetId, nextValues, successMessage) {
    let updatedSections = null;

    setFormData((prev) => {
      if (!prev?.sections) return prev;

      updatedSections = prev.sections.map((section) => {
        const sectionId = Number(section?.sectionId || section?.id || 0);
        if (sectionId !== targetId) return section;

        return {
          ...section,
          ...nextValues,
        };
      });

      return {
        ...prev,
        sections: updatedSections,
      };
    });

    setHasChanges(true);

    if (!props?.cttId || !Array.isArray(updatedSections) || updatedSections.length === 0) {
      toast.info(successMessage);
      return;
    }

    try {
      await persistSectionOrderAsync(getOrderedSections(updatedSections));
      toast.info(successMessage);
    } catch (error) {
      console.error("Failed to persist section action", error);
      toast.error("Section action could not be saved. Click Save to retry.");
    }
  }

  async function onRevertSectionToMasterContent() {
    const targetId = Number(sectionActionTarget?.sectionId || sectionActionTarget?.id || 0);
    if (!targetId) {
      closeSectionActionModal();
      return;
    }

    closeSectionActionModal();

    if (!props?.cttId) {
      toast.error("Unable to revert section content right now.");
      return;
    }

    try {
      const resp = await RightTimeApi.putAsync(`/CompanyTestType/${props.cttId}/Section/${targetId}/RevertToMasterContent`);
      if (!(resp && resp.ok)) {
        toast.error("Failed to revert section to master content.");
        return;
      }

      const payload = resp.jsonData || {};
      const masterContentId = Number(payload.sectionContentId || payload.SectionContentId || 0) || null;
      const masterContentName = payload.sectionContentName || payload.SectionContentName || "";

      applySectionActionById(targetId, {
        customSectionId: null,
        CustomSectionId: null,
        sectionContentId: masterContentId,
        SectionContentId: masterContentId,
        customizedSectionName: "",
        sectionContentName: masterContentName,
      });

      toast.info("Section reverted to master content.");
    } catch (error) {
      console.error("Failed to revert section to master content", error);
      toast.error("Failed to revert section to master content.");
    }
  }

  function getSectionId(section) {
    return Number(section?.sectionId || section?.id || 0) || 0;
  }

  function getSectionContentIdForPersist(section) {
    let rawContentId;

    if (Object.prototype.hasOwnProperty.call(section || {}, "sectionContentId")) {
      rawContentId = section?.sectionContentId;
    } else if (Object.prototype.hasOwnProperty.call(section || {}, "SectionContentId")) {
      rawContentId = section?.SectionContentId;
    } else if (Object.prototype.hasOwnProperty.call(section || {}, "customSectionId")) {
      rawContentId = section?.customSectionId;
    } else if (Object.prototype.hasOwnProperty.call(section || {}, "CustomSectionId")) {
      rawContentId = section?.CustomSectionId;
    }

    if (rawContentId === null || rawContentId === undefined) return null;

    const numericContentId = Number(rawContentId);
    return numericContentId > 0 ? numericContentId : null;
  }

  function getSectionRestartContentIdForPersist(section) {
    if (!isSectionRestartContentConfigurable(section)) {
      return null;
    }

    let rawContentId;

    if (Object.prototype.hasOwnProperty.call(section || {}, "restartPageContentId")) {
      rawContentId = section?.restartPageContentId;
    } else if (Object.prototype.hasOwnProperty.call(section || {}, "RestartPageContentId")) {
      rawContentId = section?.RestartPageContentId;
    }

    if (rawContentId === null || rawContentId === undefined) return null;

    const numericContentId = Number(rawContentId);
    return numericContentId > 0 ? numericContentId : null;
  }

  function getOrderedSections(sections) {
    return Array.isArray(sections)
      ? [...sections].sort(
        (a, b) => Number(a?.order ?? a?.Order ?? 0) - Number(b?.order ?? b?.Order ?? 0)
      )
      : [];
  }

  function handleSectionDragStart(e, section) {
    setDraggedSection(section);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(getSectionId(section)));
  }

  function handleSectionDragOver(e, section) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverSection(section);
  }

  function handleSectionDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverSection(null);
    }
  }

  function handleSectionDragEnd() {
    setDraggedSection(null);
    setDragOverSection(null);
  }

  function applySectionOrderUpdate(reorderedSections) {
    const normalizedSections = reorderedSections.map((section, index) => ({
      ...section,
      sectionId: getSectionId(section),
      id: getSectionId(section),
      order: (index + 1) * 10,
      Order: (index + 1) * 10,
    }));

    setFormData((prev) => {
      if (!prev?.sections) return prev;

      return {
        ...prev,
        sections: normalizedSections,
      };
    });
    setHasChanges(true);

    return normalizedSections;
  }

  function deleteSectionFromTestType(e, sectionToDelete) {
    e.stopPropagation();
    const sectionIdToDelete = getSectionId(sectionToDelete);
    if (!sectionIdToDelete || !formData?.sections) return;

    const remainingSections = getOrderedSections(formData.sections)
      .filter((section) => getSectionId(section) !== sectionIdToDelete);

    applySectionOrderUpdate(remainingSections);
  }

  async function persistSectionOrderAsync(normalizedSections) {
    if (!props?.cttId || !Array.isArray(normalizedSections) || normalizedSections.length === 0) {
      return;
    }

    const payload = {
      sections: normalizedSections.map((section) => ({
        sectionId: getSectionId(section),
        order: Number(section?.order ?? section?.Order ?? 0),
        sectionContentId: getSectionContentIdForPersist(section),
        restartPageContentId: getSectionRestartContentIdForPersist(section),
      })),
    };

    const resp = await RightTimeApi.putAsync(`/CompanyTestType/${props.cttId}/SectionOrder`, payload);
    if (!(resp && resp.ok)) {
      throw new Error("Failed to persist section order");
    }
  }

  async function handleSectionDropAtTarget(e, targetSection) {
    e.preventDefault();
    setDragOverSection(null);

    const draggedSectionId = getSectionId(draggedSection);
    const targetSectionId = getSectionId(targetSection);
    if (!draggedSectionId || !targetSectionId || draggedSectionId === targetSectionId || !formData?.sections) {
      setDraggedSection(null);
      return;
    }

    const orderedSections = getOrderedSections(formData.sections);
    const draggedIndex = orderedSections.findIndex((section) => getSectionId(section) === draggedSectionId);
    const targetIndex = orderedSections.findIndex((section) => getSectionId(section) === targetSectionId);
    if (draggedIndex < 0 || targetIndex < 0) {
      setDraggedSection(null);
      return;
    }

    const reorderedSections = [...orderedSections];
    const [movedSection] = reorderedSections.splice(draggedIndex, 1);
    // When moving down, removing the dragged item shifts the target left by 1,
    // so use targetIndex - 1 to keep the drop landing before the target row.
    const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    reorderedSections.splice(insertIndex, 0, movedSection);
    const normalizedSections = applySectionOrderUpdate(reorderedSections);
    setDraggedSection(null);

    try {
      await persistSectionOrderAsync(normalizedSections);
    } catch (error) {
      console.error("Failed to save section order", error);
      toast.error("Section order could not be saved. Click Save to retry.");
    }
  }

  async function handleSectionDropAtEnd(e) {
    e.preventDefault();
    setDragOverSection({ id: "drop-zone-end" });

    const draggedSectionId = getSectionId(draggedSection);
    if (!draggedSectionId || !formData?.sections) {
      setDraggedSection(null);
      setDragOverSection(null);
      return;
    }

    const orderedSections = getOrderedSections(formData.sections);
    const draggedIndex = orderedSections.findIndex((section) => getSectionId(section) === draggedSectionId);
    if (draggedIndex < 0) {
      setDraggedSection(null);
      setDragOverSection(null);
      return;
    }

    const reorderedSections = [...orderedSections];
    const [movedSection] = reorderedSections.splice(draggedIndex, 1);
    reorderedSections.push(movedSection);
    const normalizedSections = applySectionOrderUpdate(reorderedSections);
    setDraggedSection(null);
    setDragOverSection(null);

    try {
      await persistSectionOrderAsync(normalizedSections);
    } catch (error) {
      console.error("Failed to save section order", error);
      toast.error("Section order could not be saved. Click Save to retry.");
    }
  }

  async function onRemoveSectionContentSelection() {
    const targetId = Number(sectionActionTarget?.sectionId || sectionActionTarget?.id || 0);
    if (!targetId) {
      closeSectionActionModal();
      return;
    }

    closeSectionActionModal();

    if (!props?.cttId) {
      toast.error("Unable to remove section right now.");
      return;
    }

    try {
      const resp = await RightTimeApi.deleteAsync(`/CompanyTestType/${props.cttId}/Section/${targetId}`);
      if (!(resp && resp.ok)) {
        toast.error("Failed to remove section.");
        return;
      }

      toast.success("Section removed.");
      await fetchTestTypeData();
    } catch (error) {
      console.error("Failed to remove section", error);
      toast.error("Failed to remove section.");
    }
  }

  async function onShowQr(e) {
    const code = e.currentTarget.getAttribute("data-code");
    if (!code) return;
    setQrContent(`${import.meta.env.VITE_TEST_RUNNER_URL}${code}`);
    setIsQrModalOpen(true);
  }

  async function onCopyCttCodeUrl(e) {
    const code = e.currentTarget.getAttribute("data-code");
    if (!code) return;
    const url = `${import.meta.env.VITE_TEST_RUNNER_URL}${code}`;
    try {
      const copied = await copyUrlAsLink(url);
      if (copied) toast.success("Company Test Type URL copied to clipboard");
      else toast.error("Could not copy URL to clipboard");
    } catch (error) {
      console.error("Failed to copy CTT code URL:", error);
      toast.error("Could not copy URL to clipboard");
    }
  }

  function onCloseQrModal() {
    setIsQrModalOpen(false);
  }

  async function fetchCttCodes(cttId) {
    const id = cttId || props.cttId;
    if (!id) return;
    setCodesLoading(true);
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyTestType/${id}/Codes`);
      if (resp?.ok) {
        setCttCodes(Array.isArray(resp.jsonData) ? resp.jsonData : []);
      } else {
        setCttCodes([]);
      }
    } catch (error) {
      console.error("Error loading CTT codes:", error);
      setCttCodes([]);
    } finally {
      setCodesLoading(false);
    }
  }

  async function fetchCttCodeCounts(cttId) {
    const id = cttId || props.cttId;
    if (!id) return;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyTestType/${id}/Codes/Counts`);
      const next = {};
      if (resp?.ok && Array.isArray(resp.jsonData)) {
        for (const row of resp.jsonData) {
          const code = String(row?.code || "").trim();
          if (!code) continue;
          next[code] = Number(row?.testCount) || 0;
        }
      }
      setCttCodeCounts(next);
    } catch (error) {
      console.error("Error loading CTT code counts:", error);
      setCttCodeCounts({});
    }
  }

  async function toggleCttCodeActiveAsync(e, row) {
    e.stopPropagation();
    if (!props.cttId || !row?.id) return;

    const currentlyActive = row.status === "Active";
    let startUtc = "";
    let endUtc = "";
    if (row.startDateTime) {
      const startDate = new Date(row.startDateTime);
      if (Number.isNaN(startDate.getTime())) {
        toast.error("Invalid start date on code");
        return;
      }
      startUtc = startDate.toISOString();
    }
    if (row.endDateTime) {
      const endDate = new Date(row.endDateTime);
      if (Number.isNaN(endDate.getTime())) {
        toast.error("Invalid end date on code");
        return;
      }
      endUtc = endDate.toISOString();
    }

    const next = applyCttCodeActiveToggle(startUtc, endUtc, currentlyActive);
    const body = cttCodeDatesForApi(next.startUtc, next.endUtc);

    try {
      const resp = await RightTimeApi.putAsync(
        `/CompanyTestType/${props.cttId}/Codes/${row.id}`,
        body
      );
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Failed to update code status");
        return;
      }
      toast.success(currentlyActive ? "Code deactivated" : "Code activated");
      await fetchCttCodes(props.cttId);
      if (activeTab === "codes") {
        await fetchCttCodeCounts(props.cttId);
      }
    } catch (error) {
      console.error("Error toggling CTT code status:", error);
      toast.error("Failed to update code status");
    }
  }

  function proctorFieldColumns() {
    const fields = Array.isArray(formData?.fields) ? formData.fields : [];
    return fields
      .filter((field) => field?.proctorProvided)
      .slice()
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0) || (a.id || 0) - (b.id || 0));
  }

  function hasRequiredProctorFields() {
    return formData?.fields?.some((field) => field?.proctorProvided && field?.required);
  }

  async function refreshCodesTable() {
    await fetchCttCodes(props.cttId);
    await fetchCttCodeCounts(props.cttId);
  }

  async function createBlankCttCode() {
    if (!props.cttId) return;
    try {
      const resp = await RightTimeApi.postAsync(`/CompanyTestType/${props.cttId}/Codes`, {});
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Failed to create code");
        return;
      }
      toast.success("Code created");
      await refreshCodesTable();
    } catch (error) {
      console.error("Error creating CTT code:", error);
      toast.error("Failed to create code");
    }
  }

  function openAddCttCodeModal() {
    if (hasRequiredProctorFields()) {
      setIsReusableCodeRequestOpen(true);
      return;
    }
    createBlankCttCode();
  }

  function openEditCttCodeModal(row) {
    setEditingCttCode(row);
    setIsCttCodeEditOpen(true);
  }

  function closeCttCodeEditModal() {
    setIsCttCodeEditOpen(false);
    setEditingCttCode(null);
  }

  function handleClose() {
    setFormData(null);
    setCttCodes([]);
    setCttCodeCounts(null);
    closeCttCodeEditModal();
    props.onClose();
  }

  function getContentId(content) {
    return Number(content?.contentId || content?.id || 0) || 0;
  }

  function getContentName(content) {
    return content?.name || content?.contentTypeName || "";
  }

  function getSelectedContentRecord(targetField, targetContentTypeId) {
    const selectedId = Number(formData?.companyTestType?.[targetField] || 0);
    if (!selectedId || !Array.isArray(activeTestType?.contents)) return null;

    return activeTestType.contents.find((content) => {
      const contentId = getContentId(content);
      const contentTypeId = Number(content?.contentTypeId || content?.contentType || 0);
      return contentId === selectedId && contentTypeId === Number(targetContentTypeId);
    }) || null;
  }

  function getSelectedContentName(targetField, targetContentTypeId) {
    const selectedContent = getSelectedContentRecord(targetField, targetContentTypeId);
    return getContentName(selectedContent) || "none";
  }

  function getDefaultContentIdForType(targetContentTypeId) {
    if (!Array.isArray(activeTestType?.contents)) return null;

    const matches = activeTestType.contents
      .filter((content) => Number(content?.contentTypeId || content?.contentType || 0) === Number(targetContentTypeId))
      .map((content) => getContentId(content))
      .filter((id) => id > 0)
      .sort((a, b) => a - b);

    return matches.length > 0 ? matches[0] : null;
  }

  function resolveContentSelectInitialId(assignedId, targetContentTypeId) {
    const explicitId = Number(assignedId || 0);
    if (explicitId > 0) return explicitId;
    return getDefaultContentIdForType(targetContentTypeId);
  }

  function openContentSelectModal(targetField, targetContentTypeId) {
    const assignedId = Number(formData?.companyTestType?.[targetField] || 0) || null;
    setContentSelectTargetField(targetField);
    setContentSelectTypeId(Number(targetContentTypeId));
    setContentSelectCurrentId(resolveContentSelectInitialId(assignedId, targetContentTypeId));
    setContentSelectScope("ctt");
    setSectionRestartContentSectionId(null);
    setSelectionMode("content");
    setShowSelectionModal(true);
  }

  function isSectionRestartContentConfigurable(section) {
    const name = String(section?.name || "").trim().toLowerCase();
    return name !== "welcome" && name !== "done";
  }

  function openSectionRestartContentSelectModal(e, section) {
    e.stopPropagation();
    if (!isSectionRestartContentConfigurable(section)) {
      return;
    }
    const sectionId = getSectionId(section);
    const assignedId = Number(section?.restartPageContentId || section?.RestartPageContentId || 0) || null;
    setContentSelectTargetField(null);
    setContentSelectTypeId(Enums.CONTENT_TYPE.RestartPage);
    setContentSelectCurrentId(resolveContentSelectInitialId(assignedId, Enums.CONTENT_TYPE.RestartPage));
    setContentSelectScope("sectionRestart");
    setSectionRestartContentSectionId(sectionId || null);
    setSelectionMode("content");
    setShowSelectionModal(true);
  }

  function getSectionRestartContentName(section) {
    const selectedId = Number(section?.restartPageContentId || section?.RestartPageContentId || 0);
    if (!selectedId || !Array.isArray(activeTestType?.contents)) return "none";

    const match = activeTestType.contents.find((content) => {
      const contentId = getContentId(content);
      const contentTypeId = Number(content?.contentTypeId || content?.contentType || 0);
      return contentId === selectedId && contentTypeId === Enums.CONTENT_TYPE.RestartPage;
    });

    return getContentName(match) || section?.restartPageContentName || section?.RestartPageContentName || "none";
  }

  function upsertSelectedContentInLists(content, contents) {
    const selectedId = getContentId(content);
    if (!selectedId) return Array.isArray(contents) ? contents : [];

    const next = Array.isArray(contents) ? [...contents] : [];
    const index = next.findIndex((item) => getContentId(item) === selectedId);
    const merged = {
      ...(index >= 0 ? next[index] : {}),
      ...content,
      id: selectedId,
      contentId: selectedId,
      contentTypeId: Number(content?.contentTypeId || content?.contentType || contentSelectTypeId || 0),
    };
    if (index >= 0) next[index] = merged;
    else next.push(merged);
    return next;
  }

  async function onSelectContentRecord(content) {
    const targetField = contentSelectTargetField;
    const selectedId = getContentId(content);
    if (!targetField || !selectedId || !formData?.companyTestType) return;

    setFormData((prev) => ({
      ...(prev || {}),
      contents: upsertSelectedContentInLists(content, prev?.contents),
      companyTestType: {
        ...(prev?.companyTestType || {}),
        [targetField]: selectedId,
      },
    }));
    setActiveTestType((prev) => ({
      ...(prev || {}),
      contents: upsertSelectedContentInLists(content, prev?.contents),
      companyTestType: {
        ...(prev?.companyTestType || {}),
        [targetField]: selectedId,
      },
    }));
    setHasChanges(true);
    onCloseSelectionModal();
  }

  async function onDeselectContentRecord() {
    const targetField = contentSelectTargetField;
    if (!targetField || !formData?.companyTestType) return;

    const updatedData = {
      ...formData,
      companyTestType: {
        ...formData.companyTestType,
        [targetField]: 0,
      },
    };

    setFormData(updatedData);
    setActiveTestType((prev) => ({
      ...(prev || {}),
      companyTestType: {
        ...(prev?.companyTestType || {}),
        [targetField]: 0,
      },
    }));
    setHasChanges(true);
    onCloseSelectionModal();
  }

  async function deleteTestTypeAsync() {
    if (!formData?.canDelete) return;
    setShowDeleteConfirm(true);
  }

  async function handleDeleteConfirm() {
    setIsDeleting(true);
    setShowDeleteConfirm(false);

    try {
      const resp = await RightTimeApi.deleteAsync(
        "/CompanyTestType/" + formData.companyTestType.id
      );

      if (resp && resp.ok) {
        if (props.updateData)
          props.updateData();
        toast.success("Test Type removed");
        handleClose();
      } else {
        toast.error("Failed to delete test type");
      }
    } catch (error) {
      console.error("Error deleting test type:", error);
      toast.error("Error deleting test type");
    } finally {
      setIsDeleting(false);
    }
  }

  function handleDeleteCancel() {
    setShowDeleteConfirm(false);
  }

  function handleImport() {
    setShowImportTestUsersModal(true);
  }

  function handleCloseImportTestUsersModal() {
    setShowImportTestUsersModal(false);
  }

  // Compare formData to originalData to determine if there are changes (deep compare)
  // Already handled by setHasChanges in updateTestAsync/onChangeCodeTimeout

  if (!props || !props.cttId) {
    return null;
  }

  if (!formData || !activeTestType) {
    // If we have a cttId but no data, show loading
    if (props.cttId) {
      return (
        <Modal
          isOpen={!!props.cttId}
          title="Loading Test Type..."
          onClose={handleClose}
          className="testTypeInfoModal"
          size="sm"
        >
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </Modal>
      );
    }
    return null;
  }
  function onMenuClick(e) {
    const action = e.currentTarget.getAttribute("tab-name");
    setActiveTab(action);
  }

  let statuses = props.lookups.testStatuses.filter(ts => activeTestType.statuses.some(s => s.id === ts.id));

  function isSelectedStatusBillable(statusId) {
    const id = Number(statusId || 0);
    return statuses.some((ts) => Number(ts?.id || 0) === id && !!ts?.billable);
  }

  function isSelectedStatusArchive(statusId) {
    const id = Number(statusId || 0);
    return statuses.some((ts) => Number(ts?.id || 0) === id && !!ts?.archive);
  }

  function statusOptionClass(ts) {
    if (ts?.billable) return "billable";
    if (ts?.archive) return "archive";
    return "";
  }

  function selectedStatusClass(statusId) {
    if (isSelectedStatusBillable(statusId)) return "billable";
    if (isSelectedStatusArchive(statusId)) return "archive";
    return "";
  }

  return (
    <>
      <Modal
        isOpen={!!props.cttId}
        title={`Test Type Information - ${formData.companyTestType.name}`}
        onClose={handleClose}
        className="testTypeInfoModal"
        footer={(
          <>
            <div className="footerLeft">
              <button
                type="button"
                className="btn btn-success iconButton"
                onClick={handleImport}
                title="Bulk import test users"
                data-help-id="importTestUsersBulk"
              >
                <TbTableImport />
              </button>
              <button
                type="button"
                className="btn btn-danger iconButton"
                onClick={deleteTestTypeAsync}
                disabled={isDeleting || !formData?.canDelete}
                title={!formData?.canDelete ? 'This test type cannot be deleted' : (isDeleting ? 'Deleting...' : 'Delete Test Type')}
              >
                {isDeleting ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <IoTrashOutline />
                )}
              </button>
            </div>
            <div className="footerRight">
              <button className="btn btn-secondary" onClick={handleClose}>
                Close
              </button>
              <button
                className={`btn btn-primary${!hasChanges ? ' disabled' : ''}`}
                onClick={saveTestAsync}
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      >
        <div className="modalTabsContainer">
          <div className="tab-container">
            <button tab-name="general" onClick={onMenuClick} className={`modal-tab ${activeTab === "general" ? "active" : ""}`}>
              General
            </button>
            <button tab-name="timeouts" onClick={onMenuClick} className={`modal-tab ${activeTab === "timeouts" ? "active" : ""}`}>
              Timeouts
            </button>
            <button tab-name="codes" onClick={onMenuClick} className={`modal-tab ${activeTab === "codes" ? "active" : ""}`}>
              Codes
            </button>
            <button tab-name="automation" onClick={onMenuClick} className={`modal-tab ${activeTab === "automation" ? "active" : ""}`}>
              Automation
            </button>
            <button tab-name="content" onClick={onMenuClick} className={`modal-tab ${activeTab === "content" ? "active" : ""}`}>
              Content
            </button>
            <button tab-name="fields" onClick={onMenuClick} className={`modal-tab ${activeTab === "fields" ? "active" : ""}`}>
              Fields
            </button>
            {activeTestType?.isBillable &&
              <button tab-name="fees" onClick={onMenuClick} className={`modal-tab ${activeTab === "fees" ? "active" : ""}`}>
                Billing
              </button>}
            <button tab-name="sections" onClick={onMenuClick} className={`modal-tab ${activeTab === "sections" ? "active" : ""}`}>
              Sections
            </button>
            <button tab-name="email" onClick={onMenuClick} className={`modal-tab ${activeTab === "email" ? "active" : ""}`}>
              Email
            </button>
          </div>
        </div>
        <div className="modalTabsBody">
          {activeTab === "general" && (
            <div className="tableWrapper factTable generalTab">
              <table>
                <caption>
                  <span className="title">General</span>
                </caption>
                <tr>
                  <th className="label right">Name</th>
                  <td>
                    <input
                      type="text"
                      value={formData?.companyTestType?.name || ''}
                      name="companyTestType.name"
                      onChange={updateTestAsync}
                    />
                  </td>
                </tr>
                <tr>
                  <th className="label right">cttId</th>
                  <td>{formData?.companyTestType?.id || ""}</td>
                </tr>
                <tr>
                  <th className="label right">TestType</th>
                  <td>{formData?.companyTestType?.testTypeName || ""} ({formData?.companyTestType?.testTypeId || ""})</td>
                </tr>
                <tr>
                  <th className="label right">EvalType</th>
                  <td>
                    <select
                      name="companyTestType.evalComparison"
                      data-type="int"
                      value={formData?.companyTestType?.evalComparison ?? Enums.TEST_EVAL_COMPARISON.None}
                      onChange={updateTestAsync}
                    >
                      <option value={Enums.TEST_EVAL_COMPARISON.None}>None</option>
                      <option value={Enums.TEST_EVAL_COMPARISON.Culture}>Culture</option>
                      <option value={Enums.TEST_EVAL_COMPARISON.EF}>{Enums.getTestEvalComparisonLabel(Enums.TEST_EVAL_COMPARISON.EF)}</option>
                      <option value={Enums.TEST_EVAL_COMPARISON.BE_SOURCE}>{Enums.getTestEvalComparisonLabel(Enums.TEST_EVAL_COMPARISON.BE_SOURCE)}</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <th className="label right">Initial Status</th>
                  <td>
                    <select
                      name="companyTestType.initialStatusId"
                      data-type="int"
                      data-help-id="CttInitialStatus"
                      value={formData?.companyTestType?.initialStatusId || 0}
                      onChange={updateTestAsync}
                      className={selectedStatusClass(formData?.companyTestType?.initialStatusId)}
                    >
                      <option value="">- Pick one -</option>
                      {statuses.map((ts) => (
                        <option key={ts.id} value={ts.id} className={statusOptionClass(ts)}>
                          {ts.display}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <th className="label right" title="Not restartable starts a new test. Section restart reopens the unfinished section from the beginning. Answer restart reopens that section on the saved page with answers filled in.">
                    Restart
                  </th>
                  <td>
                    <select
                      name="companyTestType.restartable"
                      data-type="int"
                      data-help-id="RestartableCtt"
                      value={Number(formData?.companyTestType?.restartable) || 0}
                      onChange={updateTestAsync}
                    >
                      <option value={Enums.TEST_RESTART_MODE.NotRestartable}>Not restartable</option>
                      <option value={Enums.TEST_RESTART_MODE.SectionRestart}>Section restart</option>
                      <option value={Enums.TEST_RESTART_MODE.AnswerRestart}>Answer restart</option>
                    </select>
                  </td>
                </tr>
              </table>
            </div>
          )}
          {activeTab === "timeouts" && (
            <div className="tableWrapper factTable timeoutsTab">
              <table>
                <caption>
                  <span className="title">Timeouts</span>
                </caption>
                <tr>
                  <th className="label right">Code Timeout (dd:hh:mm)</th>
                  <td>
                    <input
                      type="text"
                      value={codeTimeoutString}
                      name="companyTestType.codeTimeoutMinutes"
                      data-type="string"
                      onChange={(e) => { updateCodeTimeoutString(e.target.value); onChangeCodeTimeout(e, 'codeTimeoutMinutes'); }}
                    />
                  </td>
                </tr>
                <tr>
                  <th className="label right">Test Timeout (dd:hh:mm)</th>
                  <td>
                    <input
                      type="text"
                      value={testTimeoutString}
                      name="companyTestType.TimeoutMinutes"
                      data-type="int"
                      onChange={(e) => { updateTestTimeoutString(e.target.value); onChangeCodeTimeout(e, 'TimeoutMinutes'); }}
                    />
                  </td>
                </tr>
                <tr>
                  <th className="label right">Timeout Status</th>
                  <td>
                    <select
                      name="companyTestType.timedoutStatusId"
                      data-type="int"
                      value={formData?.companyTestType?.timedoutStatusId || 0}
                      onChange={updateTestAsync}
                      className={selectedStatusClass(formData?.companyTestType?.timedoutStatusId)}
                    >
                      <option value="">- Pick one -</option>
                      {statuses.map((ts) => (
                        <option key={ts.id} value={ts.id} className={statusOptionClass(ts)}>
                          {ts.display}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              </table>
            </div>
          )}
          {activeTab === "codes" && (
            <CodesTable
              proctorColumns={proctorFieldColumns()}
              codesLoading={codesLoading}
              cttCodes={cttCodes}
              cttCodeCounts={cttCodeCounts}
              openAddCttCodeModal={openAddCttCodeModal}
              toggleCttCodeActiveAsync={toggleCttCodeActiveAsync}
              openEditCttCodeModal={openEditCttCodeModal}
              onShowQr={onShowQr}
              onCopyCttCodeUrl={onCopyCttCodeUrl}
              formatCttCodeDateTime={formatCttCodeDateTime}
            />
          )}
          {activeTab === "automation" && (
            <div className="tableWrapper factTable automationTab">
              <table>
                <caption>
                  <span className="title">Automation</span>
                </caption>
                <tr>
                  <th className="label right">Automatable</th>
                  <td
                    onClick={updateTestAsync}
                    className="check"
                    data-name="companyTestType.automatable"
                    data-type="toggle"
                  >
                    {formData?.companyTestType?.automatable ? (
                      <MdCheckBox className="checkbox checked actionIcon" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="label right">Grading File</th>
                  <td>
                    <input
                      type="text"
                      value={formData?.companyTestType?.gradingFile || ''}
                      name="companyTestType.gradingFile"
                      onChange={updateTestAsync}
                      disabled={!formData?.companyTestType?.automatable}
                    />
                  </td>
                </tr>
                <tr>
                  <th className="label right">Format</th>
                  <td>
                    <select
                      value={formData?.companyTestType?.gradingDataFormat ?? 20}
                      onChange={updateTestAsync}
                      name="companyTestType.gradingDataFormat"
                      data-type="int"
                      disabled={!formData?.companyTestType?.automatable}
                    >
                      <option value={20}>Format 20</option>
                      <option value={40}>Format 40</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <th className="label right">Allow Retest</th>
                  <td
                    onClick={updateTestAsync}
                    className="check"
                    data-name="companyTestType.allowRetests"
                    data-type="toggle"
                  >
                    {formData?.companyTestType?.allowRetests ? (
                      <MdCheckBox className="checkbox checked actionIcon" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="label right" title="When a retest fails (status of 'U'), what status should the retest be put into?">
                    Retest Failure Status
                  </th>
                  <td>
                    <select
                      value={formData?.companyTestType?.failureStatusId || 0}
                      onChange={updateTestAsync}
                      name="companyTestType.failureStatusId"
                      data-type="int"
                      disabled={!formData?.companyTestType?.allowRetests}
                      className={selectedStatusClass(formData?.companyTestType?.failureStatusId)}
                    >
                      {statuses.map((ts) => (
                        <option key={ts.id} value={ts.id} className={statusOptionClass(ts)}>
                          {ts.display}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <th className="label right">Notify on Completion</th>
                  <td
                    onClick={updateTestAsync}
                    className="check"
                    data-name="companyTestType.notifyOnDone"
                    data-type="toggle"
                  >
                    {formData?.companyTestType?.notifyOnDone ? (
                      <MdCheckBox className="checkbox checked actionIcon" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                    )}
                  </td>
                </tr>
                {formData?.companyTestType?.notifyOnDone && (
                  <tr>
                    <th className="label right">Completion Notification Recipients</th>
                    <td>
                      <textarea
                        value={formData?.companyTestType?.doneNotificationRecipients || ''}
                        onChange={updateTestAsync}
                        name="companyTestType.doneNotificationRecipients"
                        data-type="string"
                        style={{ width: '100%' }}
                        rows={3}
                      ></textarea>
                    </td>
                  </tr>
                )}
                <tr>
                  <th className="label right">Grading Algo</th>
                  <td>
                    <select
                      value={formData?.companyTestType?.gradingAlgoId || 0}
                      onChange={updateTestAsync}
                      name="companyTestType.gradingAlgoId"
                      data-type="int"
                    >
                      <option value={0}>Select one</option>
                      {props.lookups?.gradingAlgos &&
                        props.lookups.gradingAlgos.map((algo) => (
                          <option key={algo.id} value={algo.id}>
                            {algo.name}
                          </option>
                        ))}
                    </select>
                  </td>
                </tr>
              </table>
            </div>
          )}
          {activeTab === "fees" && (
            <div className="tableWrapper factTable feesTab">
              <table>
                <caption>
                  <span className="title">Fees</span>
                </caption>
                <tr>
                  <th className="label right">Fee Schedule Item</th>
                  <td>
                    {props?.data?.contents && props.data.contents.length > 0 && (
                      <select
                        value={formData?.companyTestType?.feeScheduleItemId || 0}
                        onChange={updateTestAsync}
                        name="companyTestType.feeScheduleItemId"
                        data-type="int"
                      >
                        <option value="">none</option>
                        {props.lookups?.fees &&
                          props.lookups.fees
                            .map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.itemName}
                              </option>
                            ))}
                      </select>
                    )}
                  </td>
                </tr>
              </table>
            </div>
          )}
          {activeTab === "content" && (
            <div className="tableWrapper factTable contentTab">
              <table>
                <caption>
                  <span className="title">Content</span>
                </caption>
                <tbody>
                  <tr>
                    <th className="label right">Supported Languages</th>
                    <td>
                      {props.lookups && props.lookups.languages && props.lookups.languages.map((lang) => (
                        <span key={lang.id} data-language={lang.id} onClick={handleLanguageToggle} className={`languageToggle checkboxArea ${formData?.companyTestType?.enabledLanguages?.includes(lang.id) ? "checked" : "unchecked"}`}>
                          {formData?.companyTestType?.enabledLanguages?.includes(lang.id) ? (
                            <MdCheckBox className="checkbox checked" />
                          ) : (
                            <MdCheckBoxOutlineBlank className="checkbox unchecked" />
                          )}
                          {getLanguageAdminLabel(lang)}
                        </span>))}
                    </td>
                  </tr>
                  {CTT_CONTENT_TAB_ROWS.map((row) => (
                    <tr
                      key={row.field}
                      className="contentSelectionRow"
                      data-content-row={row.field}
                      onClick={() => openContentSelectModal(row.field, row.contentTypeId)}
                      title="Select content"
                    >
                      <th className="label right">{row.label}</th>
                      <td className={row.cellClassName}>
                        <span className={getSelectedContentRecord(row.field, row.contentTypeId) ? "" : "defaultSectionContentName"}>
                          {getSelectedContentName(row.field, row.contentTypeId)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {activeTab === "fields" && (
            <RequestFormFieldsTable
              data={props?.data ?? { fieldTypes: [] }}
              activeTestType={activeTestType ?? formData}
              tabData={{
                company: {
                  id: formData?.companyTestType?.companyId,
                  departmentsEnabled: props.tabData?.company?.departmentsEnabled,
                },
                updateSpinner: props.updateSpinner || (() => { })
              }}
              updateData={async () => {
                await fetchTestTypeData();
                if (props.updateData) props.updateData();
              }}
            />
          )}
          {activeTab === "email" && (
            <>
              <div className="tableWrapper factTable emailTabTriggers">
                <table>
                  <caption>
                    <span className="title">Event Triggers</span>
                  </caption>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Enabled</th>
                      <th>Template</th>
                      <th>Recipient</th>
                      <th>Fixed Addresses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(formData?.emailTriggers || []).map((trigger) => (
                      <tr key={trigger.eventType}>
                        <td>{trigger.eventType}</td>
                        <td className="check" onClick={() => toggleEmailTriggerEnabled(trigger.eventType)}>
                          {trigger.isEnabled ? (
                            <MdCheckBox className="checkbox checked actionIcon" />
                          ) : (
                            <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                          )}
                        </td>
                        <td>
                          <select
                            title={`Email template for ${trigger.eventType}`}
                            value={trigger.emailTemplateId || 0}
                            onChange={(e) =>
                              updateEmailTrigger(trigger.eventType, {
                                emailTemplateId: Number(e.target.value) || null,
                              })
                            }
                          >
                            <option value={0}>— None —</option>
                            {companyEmailTemplates.map((template) => (
                              <option key={`${template.scope}-${template.id}`} value={template.id}>
                                {template.name} ({template.identifier})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            title={`Recipient for ${trigger.eventType}`}
                            value={trigger.recipientConfig?.type || "candidate"}
                            onChange={(e) =>
                              updateEmailTrigger(trigger.eventType, {
                                recipientConfig: {
                                  ...(trigger.recipientConfig || {}),
                                  type: e.target.value,
                                },
                              })
                            }
                          >
                            {RECIPIENT_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="emailRecipientFixed"
                            type="text"
                            title={`Fixed recipient addresses for ${trigger.eventType}`}
                            placeholder="email1@example.com, email2@example.com"
                            value={trigger.recipientConfig?.addresses || ""}
                            disabled={(trigger.recipientConfig?.type || "candidate") !== "fixed"}
                            onChange={(e) =>
                              updateEmailTrigger(trigger.eventType, {
                                recipientConfig: {
                                  ...(trigger.recipientConfig || { type: "fixed" }),
                                  type: "fixed",
                                  addresses: e.target.value,
                                },
                              })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="tableWrapper factTable emailTabStatusEmails">
                <table>
                  <caption>
                    <span className="title">Status Emails</span>
                  </caption>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Template</th>
                      <th>Recipient</th>
                      <th>Fixed Addresses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(formData?.statuses || [])
                      .filter((status) => status.applicable)
                      .map((status) => (
                        <tr key={status.id}>
                          <td>{status.display || status.code}</td>
                          <td>
                            <select
                              title={`Email template for status ${status.display || status.code}`}
                              value={status.emailTemplateId || 0}
                              onChange={(e) =>
                                updateStatusEmailConfig(status.id, {
                                  emailTemplateId: Number(e.target.value) || null,
                                })
                              }
                            >
                              <option value={0}>— None —</option>
                              {companyEmailTemplates.map((template) => (
                                <option key={`status-${template.scope}-${template.id}`} value={template.id}>
                                  {template.name} ({template.identifier})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              title={`Recipient for status ${status.display || status.code}`}
                              value={status.emailRecipientConfig?.type || "candidate"}
                              onChange={(e) =>
                                updateStatusEmailConfig(status.id, {
                                  emailRecipientConfig: {
                                    ...(status.emailRecipientConfig || {}),
                                    type: e.target.value,
                                  },
                                })
                              }
                            >
                              {RECIPIENT_TYPE_OPTIONS.map((opt) => (
                                <option key={`status-recipient-${status.id}-${opt.value}`} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="emailRecipientFixed"
                              type="text"
                              title={`Fixed recipient addresses for status ${status.display || status.code}`}
                              placeholder="email1@example.com, email2@example.com"
                              value={status.emailRecipientConfig?.addresses || ""}
                              disabled={(status.emailRecipientConfig?.type || "candidate") !== "fixed"}
                              onChange={(e) =>
                                updateStatusEmailConfig(status.id, {
                                  emailRecipientConfig: {
                                    ...(status.emailRecipientConfig || { type: "fixed" }),
                                    type: "fixed",
                                    addresses: e.target.value,
                                  },
                                })
                              }
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {activeTab === "sections" && (
            <div className="sectionsTableContainer tableWrapper factTable sectionsTable">
              {formData?.sections && formData.sections.length > 0 ? (
                <table className="sectionsTable">
                  <caption>
                    <span className="title">Sections</span>
                    <span className="captionRight">
                      <FaPlusSquare
                        className="actionIcon plusIcon"
                        onClick={onOpenSectionPickerModal}
                        title="Add section"
                      />
                    </span>
                  </caption>
                  <thead>
                    <tr>
                      <th data-help-id="reorderColumn"></th>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Form ID</th>
                      <th>Pages</th>
                      <th data-help-id="customizeContent">Custom</th>
                      <th>Restart Page Content</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {getOrderedSections(formData.sections)
                      .map((section, index) => {
                        const hasCustomName = section?.customizedSectionName !== null
                          && section?.customizedSectionName !== undefined
                          && String(section.customizedSectionName).trim().length > 0;
                        const sectionState = getSectionActionState(section);
                        const isUsingMasterContent = sectionState.isUsingMasterContent;
                        const defaultContentName = section?.defaultSectionContentName || section?.DefaultSectionContentName || "";
                        const sectionContentDisplayName = hasCustomName && !isUsingMasterContent
                          ? section.customizedSectionName
                          : (defaultContentName || section?.sectionContentName || "");
                        const isDefaultContentDisplay = isUsingMasterContent || (!hasCustomName && !!defaultContentName);
                        const sectionId = getSectionId(section);
                        const isDraggedOver = getSectionId(dragOverSection) === sectionId;
                        const isDragging = getSectionId(draggedSection) === sectionId;

                        return (
                          <tr
                            key={sectionId || index}
                            onDragOver={(e) => handleSectionDragOver(e, section)}
                            onDragLeave={handleSectionDragLeave}
                            onDrop={(e) => handleSectionDropAtTarget(e, section)}
                            onClick={() => onOpenSectionEditModal(section)}
                            title="Edit Section Data"
                            style={{
                              cursor: "pointer",
                              opacity: isDragging ? 0.5 : 1,
                              borderTop: isDraggedOver ? "3px solid #007acc" : "none",
                            }}
                          >
                            <th
                              className="dragHandleCell"
                              data-help-id="dragHandle"
                              onClick={(e) => e.stopPropagation()}
                              draggable
                              onDragStart={(e) => handleSectionDragStart(e, section)}
                              onDragEnd={handleSectionDragEnd}
                              style={{ cursor: isDragging ? "grabbing" : "grab" }}
                            >
                              <RxDragHandleDots1
                                className="actionIcon dragHandle"
                                style={{ cursor: isDragging ? "grabbing" : "grab" }}
                              />
                            </th>
                            <th>{section.sectionId}</th>
                            <th>{section.name}</th>
                            <th>{section.formId}</th>
                            <th>{section.pages}</th>
                            <td
                              onClick={(e) => onOpenSectionSelectModal(e, section)}
                              title="Select Customized Section"
                            >
                              <span className={isDefaultContentDisplay ? "defaultSectionContentName" : ""}>{sectionContentDisplayName}</span>
                            </td>
                            <td
                              className={`sectionRestartContentCell${isSectionRestartContentConfigurable(section) ? "" : " sectionRestartContentCell--disabled"}`}
                              onClick={(e) => openSectionRestartContentSelectModal(e, section)}
                              title={isSectionRestartContentConfigurable(section) ? "Select Restart Page Content" : "Restart Page content is not available for Welcome or Done sections"}
                            >
                              <span className={getSectionRestartContentName(section) === "none" ? "defaultSectionContentName" : ""}>
                                {isSectionRestartContentConfigurable(section) ? getSectionRestartContentName(section) : "n/a"}
                              </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()} title="Delete section from test type">
                              <IoTrashOutline
                                className="actionIcon deleteIcon"
                                onClick={(e) => deleteSectionFromTestType(e, section)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    {draggedSection && (
                      <tr
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          setDragOverSection({ id: "drop-zone-end" });
                        }}
                        onDragLeave={handleSectionDragLeave}
                        onDrop={handleSectionDropAtEnd}
                        style={{
                          height: "20px",
                          borderTop: dragOverSection?.id === "drop-zone-end" ? "3px solid #007acc" : "none",
                        }}
                      >
                        <td colSpan={8} style={{ height: "20px", backgroundColor: "#f0f0f0", textAlign: "center", fontSize: "12px", color: "#666" }}>
                          Drop here to place at end
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <p>No sections configured for this test type.</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      <QrModal
        isOpen={isQrModalOpen}
        onClose={onCloseQrModal}
        content={qrContent}
      />
      <RequestModal
        isOpen={isReusableCodeRequestOpen}
        title="Create Test"
        onClose={() => setIsReusableCodeRequestOpen(false)}
        onCreated={async () => {
          setIsReusableCodeRequestOpen(false);
          await refreshCodesTable();
        }}
        companyId={formData?.companyTestType?.companyId || props.tabData?.company?.id}
        company={props.tabData?.company}
        updateSpinner={props.updateSpinner}
        adminPerms={props.tabData?.adminPerms}
        cttId={props.cttId}
        cttName={formData?.companyTestType?.name || ""}
        className="codesTabRequestModal"
      />
      <CttCodeEditModal
        isOpen={isCttCodeEditOpen}
        cttId={props.cttId}
        codeRow={editingCttCode}
        onClose={closeCttCodeEditModal}
        onSaved={async () => {
          await fetchCttCodes(props.cttId);
          if (activeTab === "codes") {
            await fetchCttCodeCounts(props.cttId);
          }
          if (props.updateData) props.updateData();
        }}
        onShowQr={(code) => {
          setQrContent(`${import.meta.env.VITE_TEST_RUNNER_URL}${code}`);
          setIsQrModalOpen(true);
        }}
      />
      <ImportTestUsersModal
        isOpen={showImportTestUsersModal}
        onClose={handleCloseImportTestUsersModal}
        cttId={props.cttId}
        updateSpinner={props.updateSpinner}
      />
      <SectionEditModal
        isOpen={showSectionEditModal}
        onClose={onCloseSectionEditModal}
        sectionContentId={sectionContentId}
        companyTestTypeId={formData?.companyTestType?.id || props.cttId}
        sectionId={editingSectionId}
        masterSectionContentId={editingDefaultSectionContentId}
        onSectionContentForked={onSectionContentForked}
        updateSpinner={props.updateSpinner}
      />
      <SectionSelectModal
        isOpen={showSelectionModal}
        onClose={onCloseSelectionModal}
        onSelect={resolveContentSelectHandler()}
        onDeselect={resolveContentDeselectHandler()}
        sectionId={selectionMode === "section" ? contentSectionId : null}
        sectionName={selectionMode === "section" ? contentSectionName : null}
        customSectionId={selectionMode === "section" ? sectionContentId : null}
        companyId={Number(formData?.companyTestType?.companyId || props.tabData?.company?.id || 0)}
        updateSpinner={props.updateSpinner}
        mode={selectionMode || "section"}
        contentTypeId={selectionMode === "content" ? contentSelectTypeId : null}
        selectedContentId={selectionMode === "content" ? contentSelectCurrentId : null}
      />
      <SectionPickerModal
        isOpen={showSectionPickerModal}
        onClose={() => setShowSectionPickerModal(false)}
        onSelect={onSelectSectionToAdd}
        excludedSectionIds={formData?.sections?.map((section) => getSectionId(section)) || []}
        updateSpinner={props.updateSpinner}
        title="Add Section"
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Test Type"
        message={`Are you sure you want to delete test type "${formData?.companyTestType?.name}"?`}
        confirmText="Yes, Delete Test Type"
        dangerText="This will permanently remove the test type and all associated props.data."
        showSecondStep={true}
        secondStepMessage={`Delete ${formData?.companyTestType?.name} permanently?`}
        secondStepWarning="This test type and all its configuration will be permanently removed from the system."
        secondStepDetail="This includes form fields, statuses, permissions, and any other settings. This action cannot be undone."
      />
      <ConfirmModal
        isOpen={showSectionActionConfirm}
        onConfirm={sectionActionUsesCustomContent ? onRevertSectionToMasterContent : onRemoveSectionContentSelection}
        onCancel={closeSectionActionModal}
        title="Section Content Delete Action"
        message={sectionActionUsesCustomContent
          ? "This section contains customized content. Please choose your desired action."
          : "Remove this section content selection?"}
        confirmText={sectionActionUsesCustomContent ? "Revert To Master Content" : "Remove Section"}
        cancelText="Cancel"
        showSecondStep={false}
        extraActions={sectionActionUsesCustomContent
          ? [{
            label: "Remove Section",
            onClick: onRemoveSectionContentSelection,
            className: "btn-danger"
          }]
          : []}
      />
    </>
  );
}

function CodesTable({
  proctorColumns,
  codesLoading,
  cttCodes,
  cttCodeCounts,
  openAddCttCodeModal,
  toggleCttCodeActiveAsync,
  openEditCttCodeModal,
  onShowQr,
  onCopyCttCodeUrl,
  formatCttCodeDateTime,
}) {
  const columnCount = 6 + proctorColumns.length;

  function displayFor(row, field) {
    const values = Array.isArray(row?.fieldValues) ? row.fieldValues : [];
    const match = values.find(
      (value) => Number(value.companyTestTypeFieldId) === Number(field.id)
    );
    return match?.display || "";
  }

  return (
    <div className="tableWrapper factTable codesTab">
      <table className="cttCodesTable">
        <caption>
          <span className="title">Codes</span>
          <span className="captionRight">
            <FaPlusSquare
              className="actionIcon plusIcon"
              onClick={openAddCttCodeModal}
              title="Add code"
            />
          </span>
        </caption>
        <thead>
          <tr>
            <th>Active</th>
            <th>Code</th>
            {proctorColumns.map((field) => (
              <th key={field.id}>{field.label || field.fieldElementName}</th>
            ))}
            <th>Start</th>
            <th>End</th>
            <th className="number" title="Non-archived tests started with this code">Tests</th>
            <th className="codesActionsCol"></th>
          </tr>
        </thead>
        <tbody>
          {codesLoading && (
            <tr>
              <td colSpan={columnCount}>Loading codes…</td>
            </tr>
          )}
          {!codesLoading && cttCodes.length === 0 && (
            <tr>
              <td colSpan={columnCount}>No codes yet.</td>
            </tr>
          )}
          {!codesLoading && cttCodes.map((row) => (
            <tr
              key={row.id}
              className={`cttCodeRow status${row.status || ""}`}
            >
              <td
                className="actions center"
                onClick={(e) => toggleCttCodeActiveAsync(e, row)}
                title={row.status === "Active" ? "Deactivate code" : "Activate code"}
              >
                {row.status === "Active" ? (
                  <MdCheckBox className="checkbox checked actionIcon" />
                ) : (
                  <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                )}
              </td>
              <td>
                <a
                  onClick={() => openEditCttCodeModal(row)}
                  title="Edit code"
                >
                  {row.code}
                </a>
              </td>
              {proctorColumns.map((field) => (
                <td key={field.id}>{displayFor(row, field)}</td>
              ))}
              <td>{formatCttCodeDateTime(row.startDateTime)}</td>
              <td>{formatCttCodeDateTime(row.endDateTime)}</td>
              <td className="number">
                {cttCodeCounts == null
                  ? "…"
                  : (cttCodeCounts[row.code] ?? 0)}
              </td>
              <td className="codesActionsCol">
                {row.canShareUrl !== false && (
                  <>
                    <LuClipboardCopy
                      className="actionIcon copyCttCodeUrl"
                      onClick={onCopyCttCodeUrl}
                      data-code={row.code}
                      title="Copy URL to clipboard"
                    />
                    <BsQrCode
                      className="actionIcon qrCode"
                      onClick={onShowQr}
                      data-code={row.code}
                      title="View QR code"
                    />
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

